import CardHeader from "components/CardHeader";
import { Project } from "models/Project";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody, Progress } from "reactstrap";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { useDashboardData } from "./DashboardData";

const day = 24 * 60 * 60 * 1000;

interface Props {
    limit?: number;
}

const ActiveProjectsOverview: React.FC<Props> = ({ limit = 3 }) => {
    const { intlLocale, t } = useOfficeTranslation();
    const formatDate = (value: string) =>
        new Intl.DateTimeFormat(intlLocale, { day: "numeric", month: "short", year: "numeric" }).format(new Date(value));
    const { projects, projectsLoading: loading, projectsFailed: failed } = useDashboardData();

    const active = useMemo(() => {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, -1);
        return projects
            .filter(project => new Date(project.start_date) <= endOfToday && new Date(project.end_date) >= startOfToday)
            .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
    }, [projects]);

    const progress = (project: Project) => {
        const start = new Date(project.start_date).getTime();
        const end = new Date(project.end_date).getTime();
        if (end <= start) return 100;
        return Math.max(0, Math.min(100, Math.round(((Date.now() - start) / (end - start)) * 100)));
    };

    const visibleProjects = active.slice(0, limit);

    return <Card className="h-100 mb-0 dashboard-widget-card">
        <CardBody>
            <CardHeader
                title={t("dashboard.activeProjectsOverview.activeProjects")}
                icon="mdi-briefcase-outline"
                count={active.length}
                actions={<div className="d-flex gap-2">
                    <Link to="/projects" className="btn btn-sm btn-light">
                        {t("dashboard.activeProjectsOverview.allProjects")} <i className="mdi mdi-arrow-right ms-1" />
                    </Link>
                </div>}
            />
            {loading && <p className="text-muted mt-3 mb-0">{t("dashboard.activeProjectsOverview.loadingProjects")}</p>}
            {failed && <p className="text-danger mt-3 mb-0">{t("dashboard.activeProjectsOverview.projectsCouldNotBeLoaded")}</p>}
            {!loading && !failed && active.length === 0 && <p className="text-muted mt-3 mb-0">
                {t("dashboard.activeProjectsOverview.thereAreCurrentlyNoActiveProjects")}
            </p>}
            {!loading && !failed && <div className="dashboard-widget-scroll">{visibleProjects.map(project => {
                const percentage = progress(project);
                const daysRemaining = Math.max(0, Math.ceil((new Date(project.end_date).getTime() - Date.now()) / day));
                return <div className="border-bottom py-3" key={project.id}>
                    <div className="d-flex justify-content-between align-items-start gap-3">
                        <div className="min-w-0">
                            <Link to={`/projects/${project.id}`} className="text-body">
                                <strong className="d-block text-truncate">{project.name}</strong>
                            </Link>
                            <small className="text-muted">
                                {formatDate(project.start_date)} – {formatDate(project.end_date)}
                            </small>
                        </div>
                        <span className="badge bg-success bg-soft text-success flex-shrink-0">{t("dashboard.activeProjectsOverview.active")}</span>
                    </div>
                    <div className="d-flex justify-content-between small mt-3 mb-2">
                        <span>{project.workers.length} {t(project.workers.length === 1 ? "common.units.worker" : "common.units.workers")}</span>
                        <span>{daysRemaining} {t(daysRemaining === 1 ? "common.units.day" : "common.units.days")} {t("common.remaining")}</span>
                    </div>
                    <Progress value={percentage} color="primary" style={{ height: 7 }} />
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <small className="text-muted">{percentage}{t("dashboard.activeProjectsOverview.percentageOfTheDuration")}</small>
                        <Link to={`/projects/${project.id}`} className="btn btn-sm btn-light">
                            {t("dashboard.activeProjectsOverview.openProject")}
                        </Link>
                    </div>
                </div>;
            })}</div>}
        </CardBody>
    </Card>;
};

export default ActiveProjectsOverview;
